/**
 * Schedule View — the Table view (Phase 11).
 *
 * Plan: docs/plans/2026-10-13-schedule-view-table-view-print-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). What it needs from other
 * files — the tick key, a start time, a chair's position, a provider number — arrives as `deps`,
 * the way checklist.js `checklistsForDay` takes alerts.js. The page passes the real functions; the
 * node-test passes the real ones loaded from source.
 *
 * ── WHAT IT IS ─────────────────────────────────────────────────────────────────────────────────
 * The same day as the grid, read as one ROW per appointment: time, chair, patient, and the visit
 * checklist's lines inline. This file holds everything about those rows that can be wrong without
 * anybody seeing an error — the order of the lines in a row, the order of the rows, the group
 * rows, the numbers in the stats line, and the remembered view / sort.
 *
 * ── ⛔ ROWS COME FROM THE PAGE'S `checklistByAptNum`, NEVER FROM A SECOND buildChecklist() ──────
 * A row is built from the entry `checklistsForDay` already made for the block and the card
 * ({ alertKeys, categories, totals }). Building a checklist again here — even with the same
 * inputs — is one more place that can drift, and a row that lists a balance beside a block with no
 * balance icon is a screen staff cannot trust with the patient at the desk. The counts are the
 * entry's own `totals`, so the row, the block's chip and the stats line cannot disagree.
 *
 * ── ⛔ AN APPOINTMENT WITH NO CHAIR IS NEVER DROPPED ────────────────────────────────────────────
 * A broken appointment can carry Op 0, and a chair can be hidden in Open Dental after patients were
 * booked into it. groupByChair puts those in a trailing group with no chair; orderAppointments
 * sorts a missing start time or an unknown chair LAST. A table that quietly shows fewer
 * appointments than Open Dental is the one failure this page exists to prevent.
 *
 * ── PREMEDICATION IS AN FYI, NEVER A TICKBOX ────────────────────────────────────────────────────
 * checklist.js makes it `kind: 'info'` because nothing records that it was taken. Flattening keeps
 * that, and puts every clinical (red) FYI first.
 *
 * ── PER BROWSER, NOT PER OFFICE ─────────────────────────────────────────────────────────────────
 * The chosen view and sort are one person's preference, like the filters, and live in localStorage
 * under THEIR OWN key — not inside the filters blob, or "Clear filters" would flip the view back.
 */

// ── The remembered view and sort ───────────────────────────────────────────────────────────────

/** What an iPad or a desktop can draw: the operatory grid, or the table. */
export const VIEWS = ['schedule', 'table'];
/**
 * What a PHONE can draw (WP-B, 2026-10-23): the timeline of cards, the real chair grid scrolled
 * sideways ("columns"), or the table scrolled sideways. A phone and an iPad keep SEPARATE choices —
 * `view` is the iPad's / desktop's, `phoneView` the phone's — so turning an iPad upright, or opening
 * the page on a phone, never rewrites what the other device remembers.
 */
export const PHONE_VIEWS = ['timeline', 'columns', 'table'];
export const TABLE_SORTS = ['time', 'op'];
/**
 * `phoneView` defaults to the TIMELINE, not to Columns (the design defaults its phone layout to
 * Columns; Nilay's decision, 2026-10-23, is that a phone opens on the cards it has always had).
 */
export const DEFAULT_TABLE_PREFS = { view: 'schedule', phoneView: 'timeline', sort: 'time' };

/** localStorage key prefix. Per site: the same browser can be signed in to several offices. */
export const TABLE_PREFS_PREFIX = 'teamio_schedule_view_table';

export function tablePrefsKey(siteId) {
  return `${TABLE_PREFS_PREFIX}:${siteId || 'unknown'}`;
}

/**
 * Total: takes anything (a parsed localStorage value from an older version, junk, null) and returns
 * a usable { view, phoneView, sort }, a FRESH object every time. Never throws — a corrupt preference
 * must not break the page. A blob saved before the phone views existed ({ view, sort }) has no
 * `phoneView`: it reads as the default timeline, and the rest is kept.
 */
export function normalizeTablePrefs(raw) {
  const r = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  return {
    view: VIEWS.indexOf(r.view) !== -1 ? r.view : DEFAULT_TABLE_PREFS.view,
    phoneView: PHONE_VIEWS.indexOf(r.phoneView) !== -1 ? r.phoneView : DEFAULT_TABLE_PREFS.phoneView,
    sort: TABLE_SORTS.indexOf(r.sort) !== -1 ? r.sort : DEFAULT_TABLE_PREFS.sort,
  };
}

function isDefaultPrefs(prefs) {
  return prefs.view === DEFAULT_TABLE_PREFS.view
    && prefs.phoneView === DEFAULT_TABLE_PREFS.phoneView
    && prefs.sort === DEFAULT_TABLE_PREFS.sort;
}

/**
 * The view that is DRAWN: the phone's own choice on a phone, the iPad's / desktop's everywhere else.
 * Reads only — the other device's remembered choice is never touched. Anything unknown (junk, a phone
 * value on a desktop, nothing at all) is that device's default, never `undefined`: a blank page is
 * the one answer the front desk cannot recover from.
 *
 * @param {boolean} isPhone  the page's phone breakpoint (layout.js PHONE_MEDIA_QUERY)
 * @param {Object}  prefs    { view, phoneView } — the store's state
 */
export function effectiveView(isPhone, prefs) {
  const p = normalizeTablePrefs(prefs);
  return isPhone ? p.phoneView : p.view;
}

/**
 * The tabs of the view switch: Schedule · Table view on an iPad / desktop, Timeline · Columns · Table
 * on a phone. `test` is the data-test hook the e2e spec and the real-page script click; `icon` is a
 * font-awesome class. A FRESH array of fresh objects every call.
 */
export function viewTabs(isPhone) {
  if (isPhone) {
    return [
      { key: 'timeline', label: 'Timeline', icon: 'fa-list', test: 'sv-view-timeline' },
      { key: 'columns', label: 'Columns', icon: 'fa-columns', test: 'sv-view-columns' },
      { key: 'table', label: 'Table', icon: 'fa-table', test: 'sv-view-table' },
    ];
  }
  return [
    { key: 'schedule', label: 'Schedule', icon: 'fa-calendar', test: 'sv-view-schedule' },
    { key: 'table', label: 'Table view', icon: 'fa-table', test: 'sv-view-table' },
  ];
}

/**
 * Read the remembered view and sort. `storage` is injected so the tests can pass a fake — and so a
 * browser that throws on localStorage (Safari private browsing does) degrades to the defaults
 * instead of a blank page.
 */
export function readTablePrefs(storage, siteId) {
  try {
    if (!storage) return normalizeTablePrefs(null);
    const raw = storage.getItem(tablePrefsKey(siteId));
    if (!raw) return normalizeTablePrefs(null);
    return normalizeTablePrefs(JSON.parse(raw));
  } catch (e) {
    return normalizeTablePrefs(null);
  }
}

/**
 * Persist. The defaults REMOVE the key (nothing left behind to read back). Returns whether it
 * stuck, so a caller can decide whether to promise anything.
 */
export function writeTablePrefs(storage, siteId, prefs) {
  try {
    if (!storage) return false;
    const next = normalizeTablePrefs(prefs);
    if (isDefaultPrefs(next)) {
      storage.removeItem(tablePrefsKey(siteId));
      return true;
    }
    storage.setItem(tablePrefsKey(siteId), JSON.stringify(next));
    return true;
  } catch (e) {
    return false;
  }
}

// ── One row's lines ────────────────────────────────────────────────────────────────────────────

/**
 * The short tag beside each line (the design's HD_CAT). Every key in checklist.js
 * CHECKLIST_CATEGORIES must be here and nothing else — pinned by
 * cypress/node-tests/schedule-view-table-view.test.js ("AGREEMENT").
 */
export const CATEGORY_TAGS = {
  medical: 'MED',
  insurance: 'INS',
  forms: 'FORM',
  tasks: 'TASK',
  followups: 'F/U',
  ar: 'AR',
  treatment: 'TX',
};

/**
 * The front desk's order for to-dos: before they arrive → at check-in → before they leave. Every
 * `phase` checklist.js emits must be here (pinned by the same node-test, which reads the source).
 */
export const PHASE_ORDER = ['prep', 'arrive', 'leave'];

/** A phase the table can order. Missing or unknown counts as "before arrival" — the earliest. */
function phaseRank(phase) {
  const i = PHASE_ORDER.indexOf(phase);
  return i === -1 ? 0 : i;
}

function lineFor(catKey, item) {
  const isTodo = item.kind === 'action';
  return {
    id: item.id,
    cat: catKey,
    tag: Object.prototype.hasOwnProperty.call(CATEGORY_TAGS, catKey) ? CATEGORY_TAGS[catKey] : '',
    // checklist.js (checklistTotals) treats anything that is not 'action' as an FYI; so does this.
    kind: isTodo ? 'action' : 'info',
    tone: item.tone || '',
    // A phase belongs to a to-do. An FYI has nothing to do and so no phase.
    phase: isTodo ? PHASE_ORDER[phaseRank(item.phase)] : null,
    text: item.short || item.title || '',
    // Copied, so a cell can never edit the checklist the block and the card are reading.
    meta: item.meta ? Object.assign({}, item.meta) : null,
  };
}

/**
 * One ordered list for the Alerts cell, from a `checklistByAptNum[apt].categories`:
 *   1. clinical FYIs (`info` + tone `crit` — premedication), 2. every other FYI,
 *   3. to-dos by PHASE, then category order, then the order checklist.js gave them.
 * Stable — the same input gives the same order every time — and the input is never changed.
 *
 * @returns {Array} [{ id, cat, tag, kind, tone, phase, text, meta }]
 */
export function flattenChecklist(categories) {
  const critical = [];
  const others = [];
  const todos = [];
  (Array.isArray(categories) ? categories : []).forEach((category) => {
    if (!category || !Array.isArray(category.items)) return;
    category.items.forEach((item) => {
      if (!item) return;
      const line = lineFor(category.key, item);
      if (line.kind === 'action') todos.push(line);
      else if (line.tone === 'crit') critical.push(line);
      else others.push(line);
    });
  });
  // Sorted on the phase alone, with the arrival position as the tiebreak — so "stable" does not
  // depend on the browser's sort being stable (older iPads).
  const ranked = todos
    .map((line, position) => ({ line, position, rank: phaseRank(line.phase) }))
    .sort((a, b) => (a.rank - b.rank) || (a.position - b.position))
    .map(entry => entry.line);
  return critical.concat(others, ranked);
}

/**
 * The row for one appointment.
 *
 * `todo` / `done` / `fyi` are the checklist entry's own `totals` — not recounted here — so the row,
 * the block's chip and the stats line cannot disagree. `checked` on each line says whether THIS
 * visit has ticked it (never true for an FYI). An appointment with no entry yet (the alerts have
 * not arrived) is an empty row that still carries its ids.
 *
 * ⛔ `deps.checkKey` (checklist.js checkKey) is REQUIRED. Without it every box would draw unticked
 * while ticks exist — a screen that claims nothing was done — so it throws instead of guessing.
 *
 * @param {Object} appt        the appointment
 * @param {Object} [checklist] checklistByAptNum[appt.AptNum]: { alertKeys, categories, totals }
 * @param {Object} [checks]    the store's ticks, '<AptNum>:<ItemKey>' → { by, at }
 * @param {Object} deps        { checkKey }
 * @returns {Object} { aptNum, patNum, items, todo, done, fyi, allDone }
 */
export function rowFor(appt, checklist, checks, deps) {
  const d = deps || {};
  if (typeof d.checkKey !== 'function') throw new TypeError('rowFor needs deps.checkKey (checklist.js checkKey)');
  const aptNum = appt ? appt.AptNum : null;
  const entry = checklist && typeof checklist === 'object' ? checklist : {};
  const marks = checks || {};
  const items = flattenChecklist(entry.categories).map(line => Object.assign({}, line, {
    checked: line.kind === 'action' && !!marks[d.checkKey(aptNum, line.id)],
  }));
  const totals = entry.totals || {};
  const todo = Number(totals.todo) || 0;
  const done = Number(totals.done) || 0;
  const fyi = Number(totals.fyi) || 0;
  return {
    aptNum,
    patNum: appt ? appt.PatNum : null,
    items,
    todo,
    done,
    fyi,
    // Nothing to do is not "all done": no green ring on a row that never had a to-do.
    allDone: todo > 0 && done >= todo,
  };
}

// ── The order of the rows, and the group rows ──────────────────────────────────────────────────

/** Ascending, with anything that is not a finite number LAST. */
function compareLast(a, b) {
  const aOk = Number.isFinite(a);
  const bOk = Number.isFinite(b);
  if (!aOk && !bOk) return 0;
  if (!aOk) return 1;
  if (!bOk) return -1;
  return a - b;
}

/**
 * The appointments in table order — a sorted COPY, the input is never touched.
 *   • `time`: start, then chair, then AptNum.   • `op`: chair, then start, then AptNum.
 * No start time, or a chair that is not in the day, sorts LAST — never first, never dropped.
 * Anything but 'op' is By time.
 *
 * @param {Array}    appointments
 * @param {string}   sort   'time' | 'op'
 * @param {Object}   deps   { startOf(appt) → minutes|null, opIndexOf(appt) → chair index or -1 }
 */
export function orderAppointments(appointments, sort, deps) {
  const d = deps || {};
  if (typeof d.startOf !== 'function' || typeof d.opIndexOf !== 'function') {
    throw new TypeError('orderAppointments needs deps.startOf and deps.opIndexOf');
  }
  const chairFirst = sort === 'op';
  return (Array.isArray(appointments) ? appointments : [])
    .filter(Boolean)
    .map((appt, position) => {
      const op = d.opIndexOf(appt);
      return {
        appt,
        position,
        start: d.startOf(appt),
        // A chair index below zero is "not in the day": no chair at all, so last.
        chair: Number.isFinite(op) && op >= 0 ? op : null,
        aptNum: Number(appt.AptNum),
      };
    })
    .sort((a, b) => {
      const byStart = compareLast(a.start, b.start);
      const byChair = compareLast(a.chair, b.chair);
      const primary = chairFirst ? byChair : byStart;
      const secondary = chairFirst ? byStart : byChair;
      return primary || secondary || compareLast(a.aptNum, b.aptNum) || (a.position - b.position);
    })
    .map(entry => entry.appt);
}

/**
 * Group rows for By operatory: [{ op, appointments }] in the day's chair order, only chairs that
 * have somebody in them, plus a trailing `{ op: null, appointments }` for every appointment whose
 * chair is not in the list. ⛔ Those are NOT dropped (see the header). Appointments keep the order
 * they were handed over in, so sort first.
 */
export function groupByChair(appointments, operatories) {
  const slots = {};
  const groups = [];
  (Array.isArray(operatories) ? operatories : []).forEach((op) => {
    if (!op) return;
    const key = String(op.OperatoryNum);
    if (Object.prototype.hasOwnProperty.call(slots, key)) return;
    slots[key] = { op, appointments: [] };
    groups.push(slots[key]);
  });
  const unplaced = [];
  (Array.isArray(appointments) ? appointments : []).forEach((appt) => {
    if (!appt) return;
    const key = String(appt.Op);
    if (Object.prototype.hasOwnProperty.call(slots, key)) slots[key].appointments.push(appt);
    else unplaced.push(appt);
  });
  const out = groups.filter(group => group.appointments.length > 0);
  if (unplaced.length) out.push({ op: null, appointments: unplaced });
  return out;
}

/**
 * "P. Patel" — or "Dr. Patel" when the name itself starts with "Dr." (the design's hdProv). A
 * trailing credential after a comma ("Patel, DDS") is dropped. No name at all falls back to the
 * provider's abbreviation, and nothing at all to an empty string, never "undefined".
 */
export function shortProviderName(prov) {
  if (!prov) return '';
  const text = v => (v === null || v === undefined ? '' : String(v)).trim();
  const full = [text(prov.FName), text(prov.LName)].filter(Boolean).join(' ').replace(/,.*$/, '').trim();
  const words = full.split(/\s+/).filter(Boolean);
  const titled = words.length > 0 && /^dr\./i.test(words[0]);
  const names = titled ? words.slice(1) : words;
  if (!names.length) return text(prov.Abbr);
  const surname = names[names.length - 1];
  if (titled) return `Dr. ${surname}`;
  if (names.length === 1) return surname;
  return `${names[0].charAt(0).toUpperCase()}. ${surname}`;
}

/**
 * The providers to name in a group header ("Op 2 · Dr. Chen · 3 patients"): each distinct one once,
 * in order of first appearance, as shortProviderName. A provider the day does not list, an
 * appointment with none, and a provider with no name are left out.
 *
 * @param {Object} deps  { providerNumOf(appt) → ProvNum|null } — layout.js appointmentProviderNum,
 *                       so the header names the hygienist for a hygiene visit, as the phone card does
 */
export function groupProviders(appointments, providers, deps) {
  const d = deps || {};
  if (typeof d.providerNumOf !== 'function') throw new TypeError('groupProviders needs deps.providerNumOf');
  const list = Array.isArray(providers) ? providers : [];
  const seen = {};
  const names = [];
  (Array.isArray(appointments) ? appointments : []).forEach((appt) => {
    if (!appt) return;
    const num = d.providerNumOf(appt);
    if (num === null || num === undefined || seen[num]) return;
    seen[num] = true;
    const name = shortProviderName(list.find(p => p && Number(p.ProvNum) === Number(num)));
    if (name) names.push(name);
  });
  return names;
}

// ── The stats line ─────────────────────────────────────────────────────────────────────────────

/**
 * The numbers behind "12 patients · 7 to-dos open · 2 FYI".
 *
 * `patients` counts DISTINCT patients (a patient booked twice is two appointments and one patient);
 * an appointment with no usable PatNum is a row but not a patient. `open` / `done` / `fyi` are
 * summed from each appointment's checklist `totals` — the same numbers its row and chip show — so a
 * tick left behind by an item that has since cleared is not "done" here either.
 */
export function tableTotals(appointments, checklists) {
  const list = (Array.isArray(appointments) ? appointments : []).filter(Boolean);
  const byApt = checklists && typeof checklists === 'object' ? checklists : {};
  const patients = {};
  let patientCount = 0;
  let open = 0;
  let done = 0;
  let fyi = 0;
  list.forEach((appt) => {
    const patNum = Number(appt.PatNum);
    if (patNum > 0 && !patients[patNum]) {
      patients[patNum] = true;
      patientCount += 1;
    }
    const entry = byApt[appt.AptNum];
    const totals = entry && entry.totals;
    if (!totals) return;
    const todo = Number(totals.todo) || 0;
    const ticked = Number(totals.done) || 0;
    open += Math.max(todo - ticked, 0);
    done += ticked;
    fyi += Number(totals.fyi) || 0;
  });
  return { appointments: list.length, patients: patientCount, open, done, fyi };
}

/**
 * "12 patients · 7 to-dos open · 2 FYI" — or, until `/alerts` has answered,
 * "12 patients · still checking alerts". ⛔ Never "0 to-dos open" about a day nobody has checked:
 * with no `alertsReady` it assumes the alerts are NOT in.
 */
export function statsLine(totals, options) {
  const t = totals || {};
  const patients = Number(t.patients) || 0;
  const head = `${patients} ${patients === 1 ? 'patient' : 'patients'}`;
  if (!(options && options.alertsReady)) return `${head} · still checking alerts`;
  const open = Number(t.open) || 0;
  const fyi = Number(t.fyi) || 0;
  return `${head} · ${open} ${open === 1 ? 'to-do' : 'to-dos'} open · ${fyi} FYI`;
}
